import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';

const IDLE_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours
const WARNING_TIME = (24 * 60 * 60 - 5 * 60) * 1000; // 5 minutes before timeout

const useIdleTimeout = () => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const isWarningShownRef = useRef(false);

  const handleLogout = useCallback(() => {
    // Clear all timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Clear session storage
    localStorage.removeItem('userInfo');
    localStorage.removeItem('patient');
    localStorage.removeItem('answers');
    sessionStorage.clear();

    // Show logout message
    message.info('Your session has expired due to inactivity. Please log in again.');

    // Redirect to landing page
    navigate('/', { replace: true });
  }, [navigate]);

  const resetTimer = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Reset warning flag
    isWarningShownRef.current = false;

    // Set warning timeout (at 4 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      if (!isWarningShownRef.current) {
        isWarningShownRef.current = true;
        message.warning({
          content: 'Your session will expire in 1 minute due to inactivity. Please click anywhere or press any key to continue.',
          duration: 60,
          style: {
            marginTop: '100px',
          },
        });
      }
    }, WARNING_TIME);

    // Set logout timeout (at 5 minutes)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT);
  }, [handleLogout]);

  useEffect(() => {
    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    const handleActivity = () => {
      resetTimer();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize the timer on component mount
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [resetTimer]);

  return { handleLogout };
};

export default useIdleTimeout;
